import React, { useState, useEffect, useRef } from 'react';
import { FaComment, FaTimes, FaMinus, FaPaperPlane, FaCog, FaEllipsisH, FaMicrophone } from 'react-icons/fa';
import { BsFillEmojiSmileFill } from 'react-icons/bs';
import { IoMdSend } from 'react-icons/io';
import './chatbot.css';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    { 
      id: 1,
      text: "Hello! I'm CECOA Assistant. How can I help you today?", 
      sender: 'bot',
      timestamp: new Date(),
      status: 'read'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [quickReplies, setQuickReplies] = useState([
    "Membership benefits",
    "Upcoming events",
    "Contact support"
  ]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
    if (!isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    if (!isMinimized) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (inputValue.trim() === '') return;

    const newMessage = {
      id: Date.now(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
      status: 'sent'
    };

    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate bot processing
    setTimeout(() => {
      setIsTyping(false);
      const botResponse = generateBotResponse(inputValue);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        text: botResponse,
        sender: 'bot',
        timestamp: new Date(),
        status: 'delivered'
      }]);
    }, 1500 + Math.random() * 2000); // Random delay for more natural feel
  };

  const generateBotResponse = (input) => {
    const lowerInput = input.toLowerCase();
    
    if (lowerInput.includes('member') || lowerInput.includes('benefit')) {
      return "Our members enjoy exclusive benefits including professional development courses, networking program, and access to our researches. Would you like me to send you the full benefits brochure?";
    } else if (lowerInput.includes('event') || lowerInput.includes('conference')) {
      return "Our next major event is 1st International Optometric Conference on 24-25 January, 2026 in Colombo.";
    } else if (lowerInput.includes('contact') || lowerInput.includes('support')) {
      return "You can reach our support team at info@optometryassociation.lk or call +94 11 2682277 during business hours (9am-5pm, Mon-Fri).";
    } else {
      const responses = [
        "I've noted your question and our team will respond as soon as possible.",
        "That's an interesting question! Let me connect you with a specialist.",
        "I can help with membership questions, event information, and general inquiries. Could you be more specific?",
        "Thanks for your message! I'm still reaching to our support team, but I'll make sure your question reaches the right person."
      ];
      return responses[Math.floor(Math.random() * responses.length)];
    }
  };

  const handleQuickReply = (reply) => {
    setInputValue(reply);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
    // In a real implementation, this would start/stop voice recording
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`chatbot-container ${isOpen ? 'open' : ''}`}>
      {!isOpen ? (
        <button className="chatbot-toggle" onClick={toggleChat}>
          <div className="pulse-dot"></div>
          <FaComment className="chat-icon" />
          <span>CEOA Assistant</span>
        </button>
      ) : (
        <div className={`chatbot-window ${isMinimized ? 'minimized' : ''}`}>
          <div className="chatbot-header">
            <div className="bot-info">
              <div className="bot-avatar">
                <img src="/user.png" alt="CEOA Assistant" />
                <span className="online-dot"></span>
              </div>
              <div className="bot-details">
                <h3>CECOA Assistant</h3>
                <p>{isTyping ? 'Typing...' : 'Online'}</p>
              </div>
            </div>
            <div className="chatbot-actions">
              <button onClick={toggleMinimize} className="minimize-btn" title="Minimize">
                {/* <FaMinus /> */}
              </button>
              <button onClick={toggleChat} className="close-btn" title="Close">
                <FaTimes />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              <div className="chatbot-messages">
                <div className="welcome-message">
                  <p>Welcome to Ceylon Eye Care Optometry Association support</p>
                  <small>Typically replies in a few minutes</small>
                </div>
                
                {messages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`message ${message.sender}`}
                  >
                    <div className="message-content">
                      {message.text}
                      <div className="message-meta">
                        <span className="timestamp">{formatTime(message.timestamp)}</span>
                        {message.sender === 'user' && (
                          <span className={`status ${message.status}`}></span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {isTyping && (
                  <div className="message bot typing-indicator">
                    <div className="typing-dots">
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {messages.length <= 1 && (
                <div className="quick-replies">
                  <p>Quick replies:</p>
                  <div className="quick-reply-buttons">
                    {quickReplies.map((reply, index) => (
                      <button 
                        key={index} 
                        className="quick-reply"
                        onClick={() => handleQuickReply(reply)}
                      >
                        {reply}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <form onSubmit={handleSendMessage} className="chatbot-input">
                <button type="button" className="emoji-btn">
                  <BsFillEmojiSmileFill />
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type your message..."
                  required
                />
                <div className="input-actions">
                  {inputValue ? (
                    <button type="submit" className="send-btn">
                      <IoMdSend />
                    </button>
                  ) : (
                    <button 
                      type="button" 
                      className={`voice-btn ${isRecording ? 'recording' : ''}`}
                      onClick={toggleRecording}
                    >
                      <FaMicrophone />
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatBot;